import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Empty, ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { useResource } from '../lib/hooks';
import type {
  CsatClientSummary,
  CsatSnippet,
  CsatStats,
  CsatTrendBucket,
  RatingKey,
} from '../lib/types';

/**
 * CSAT — the customer satisfaction survey, moved in from the standalone app.
 *
 * Two halves of one job: build the block that goes into a ticket reply, and
 * read what came back. They are tabs on one page rather than two pages because
 * the person who generates a snippet is the person who checks the score, and
 * splitting them put a nav click between the two things a service manager does
 * in the same sitting.
 *
 * The charts are plain HTML and CSS. Three stacked bars do not justify a chart
 * library, and the app's tokens already carry the colours.
 */

/* Brand Direct has no green and no amber, so the three faces run the same
   severity ramp as every other status in the app: navy for good, climbing the
   crimson accent from there. The emoji carry the happy/sad meaning — colour
   only has to separate the three, and every number is written out beside its
   bar so colour never carries meaning alone. */
export type SnippetMode = 'ticket' | 'template';

const MODES: { key: SnippetMode; label: string }[] = [
  { key: 'ticket', label: 'One ticket' },
  { key: 'template', label: 'PSA template' },
];

/**
 * Autotask's notification variables, as a starting point.
 *
 * Defaults rather than a fixed list: every PSA spells these differently, the
 * exact wording changes between Autotask versions, and the only authority on
 * what is valid is the variable picker in the template editor itself. So they
 * are editable, and the screen says to confirm them there.
 *
 * The account and primary-resource names are transcribed from a working
 * Autotask template; the plain ticket NUMBER is deliberately not the
 * "(with link)" variant, which emits an anchor tag and cannot go in a URL.
 */
const AUTOTASK_TOKENS = {
  ticketId: '[Ticket: Number]',
  client: '[Ticket: Account]',
  customer: '[Contact: Email Address]',
  agent: '[Ticket: Primary Resource]',
  subject: '[Ticket: Title]',
  prompt: '',
  footnote: '',
};

const RATINGS: { key: RatingKey; label: string; face: string; color: string }[] = [
  { key: 'green', label: 'Happy', face: '🙂', color: 'var(--status-good)' },
  { key: 'yellow', label: 'Okay', face: '😐', color: 'var(--status-serious)' },
  { key: 'red', label: 'Unhappy', face: '🙁', color: 'var(--status-critical)' },
];

const RANGES: { value: string; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'all', label: 'All time' },
];

export function CsatPage() {
  const [tab, setTab] = useState<'results' | 'builder'>('results');

  return (
    <>
      <PageHead
        title="CSAT"
        lede="Three faces at the bottom of a ticket reply. What comes back is the only unfiltered thing a customer says about the desk — and the evidence behind the CSAT gauge on Service Delivery."
      />

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'results' ? ' active' : ''}`}
          onClick={() => setTab('results')}
        >
          Results
        </button>
        <button
          type="button"
          className={`tab${tab === 'builder' ? ' active' : ''}`}
          onClick={() => setTab('builder')}
        >
          Snippet builder
        </button>
      </div>

      {tab === 'results' ? <Results /> : <SnippetBuilder />}
    </>
  );
}

/* --- Results -------------------------------------------------------------- */

function Results() {
  /**
   * The window lives in the URL, not in component state.
   *
   * That is what makes the drill-down from Service Delivery work: clicking the
   * CSAT gauge for a month is a link to `/csat?from=…&to=…`, and landing here
   * with those parameters shows exactly the responses that produced the
   * figure. It also means a range can be sent to somebody in a message.
   */
  const [params, setParams] = useSearchParams();
  const from = params.get('from');
  const to = params.get('to');
  const label = params.get('label');
  const days = params.get('days') ?? '90';
  const pinned = Boolean(from ?? to);

  const path = pinned
    ? `/csat/stats?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`
    : `/csat/stats?days=${encodeURIComponent(days)}`;

  const resource = useResource<CsatStats>(path);
  const [sort, setSort] = useState<{ column: keyof CsatClientSummary; asc: boolean }>({
    column: 'total',
    asc: false,
  });

  const stats = resource.data;

  const clients = useMemo(() => {
    if (!stats) return [];
    const dir = sort.asc ? 1 : -1;
    return [...stats.clients].sort((a, b) => {
      const av = a[sort.column];
      const bv = b[sort.column];
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv)) * dir;
      }
      /* Clients with no score sort last whichever way the column is pointing:
         "—" at the top of a CSAT ranking is noise. */
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * dir || a.client.localeCompare(b.client);
    });
  }, [stats, sort]);

  if (resource.loading) return <Loading label="Loading the responses" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  if (!stats) return null;

  const t = stats.totals;

  const setDays = (value: string) => {
    /* Choosing a day range clears a pinned month — the two cannot both be the
       answer to "what am I looking at". */
    setParams(value === '90' ? {} : { days: value }, { replace: true });
  };

  const sortBy = (column: keyof CsatClientSummary) => {
    setSort((current) =>
      current.column === column
        ? { column, asc: !current.asc }
        : { column, asc: column === 'client' },
    );
  };

  return (
    <>
      <div className="row-between wrap mb-4">
        {pinned ? (
          <div className="csat-pin">
            <span className="eyebrow">Showing</span>
            <h2>{label ?? describeWindow(from, to)}</h2>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setParams({}, { replace: true })}
            >
              Back to the last 90 days
            </button>
          </div>
        ) : (
          <div className="tabs csat-range">
            {RANGES.map((range) => (
              <button
                key={range.value}
                type="button"
                className={`tab${days === range.value ? ' active' : ''}`}
                onClick={() => setDays(range.value)}
              >
                {range.label}
              </button>
            ))}
          </div>
        )}
        <div className="row">
          <span className="xs subtle">Updated {relative(stats.generatedAt)}</span>
          {/* `refreshing`, not `loading`: a re-read of what is already on screen
              deliberately leaves `loading` false so the page is not torn down
              (see useResource), which would otherwise leave this button with
              no feedback at all. */}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={resource.refreshing}
            onClick={resource.reload}
          >
            {resource.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {stats.truncated && (
        <div className="alert mb-4">
          Showing the most recent 5,000 responses. Older responses are stored but are not in these
          totals.
        </div>
      )}

      {t.total === 0 ? (
        <Empty title="Nothing in this window">
          <p>
            No responses were submitted{' '}
            {pinned ? 'in this period' : days === 'all' ? 'at all yet' : `in the last ${days} days`}.
            Generate a snippet on the other tab and paste it into a ticket reply — a response lands
            here the moment a customer submits one.
          </p>
        </Empty>
      ) : (
        <>
          <div className="grid grid-4 mb-4">
            <Stat
              label="CSAT score"
              value={t.csat === null ? '—' : `${t.csat}%`}
              note={`${t.green} of ${t.total} rated Happy`}
            />
            <Stat label="Responses" value={String(t.total)} note={`${t.withNotes} with comments`} />
            <Stat
              label="Needs attention"
              value={String(t.red)}
              note={t.red === 1 ? '1 unhappy rating' : `${t.red} unhappy ratings`}
              flag={t.red > 0}
            />
            <Stat label="Clients" value={String(t.clients)} note="with at least one response" />
          </div>

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Volume</span>
                <h2>Responses over time</h2>
                <p className="sub">
                  Stacked happy-first, so the unhappy ratings are the part that sticks up.
                </p>
              </div>
              <Legend />
            </div>
            <div className="card-body">
              <Trend buckets={stats.trend} />
            </div>
          </section>

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">By client</span>
                <h2>Clients</h2>
                <p className="sub">
                  Attributed to the client on the snippet, or the customer&rsquo;s email domain when
                  there was not one.
                </p>
              </div>
              <Legend />
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <SortHeader column="client" sort={sort} onSort={sortBy}>
                      Client
                    </SortHeader>
                    <SortHeader column="total" sort={sort} onSort={sortBy} numeric>
                      Responses
                    </SortHeader>
                    <th>Breakdown</th>
                    <SortHeader column="csat" sort={sort} onSort={sortBy} numeric>
                      CSAT
                    </SortHeader>
                    <SortHeader column="lastAt" sort={sort} onSort={sortBy} numeric>
                      Last response
                    </SortHeader>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((row) => (
                    <tr key={row.client}>
                      <td className="strong">{row.client}</td>
                      <td className="num">{row.total}</td>
                      <td>
                        <div className="csat-dist" title={breakdown(row)}>
                          {RATINGS.map((rating) =>
                            row[rating.key] ? (
                              <i
                                key={rating.key}
                                style={{ background: rating.color, flex: row[rating.key] }}
                              />
                            ) : null,
                          )}
                        </div>
                        {/* The bar is the shape; the numbers are the fact. Both,
                            because the crimson steps are close in lightness. */}
                        <span className="xs subtle">{breakdown(row)}</span>
                      </td>
                      <td className="num strong">{row.csat === null ? '—' : `${row.csat}%`}</td>
                      <td className="num xs muted">{shortDate(row.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">In their words</span>
                <h2>Recent feedback</h2>
              </div>
            </div>
            <ul className="csat-feed">
              {stats.recent.map((response) => {
                const rating = RATINGS.find((r) => r.key === response.rating);
                return (
                  <li key={response.id}>
                    <span className="csat-feed-face" aria-hidden="true">
                      {rating?.face ?? '•'}
                    </span>
                    <div className="grow">
                      <div className="xs muted">
                        <span className="strong">{response.client ?? domainOf(response.customer)}</span>
                        {' · '}
                        <span className="mono">{response.ticketId}</span>
                        {response.agent ? ` · ${response.agent}` : ''}
                        {' · '}
                        {relative(response.submittedAt)}
                        {/* Only the unproven kind is called out. Marking both
                            would put a badge on every row and say nothing. */}
                        {response.linkKind === 'template' && (
                          <>
                            {' · '}
                            <span
                              className="subtle"
                              title="Came in on a PSA template link. The ticket, client and contact were filled in by the PSA as it sent, so they are not signed."
                            >
                              via template
                            </span>
                          </>
                        )}
                        <span className="visually-hidden"> — {rating?.label}</span>
                      </div>
                      {response.subject && <div className="small strong">{response.subject}</div>}
                      {response.notes ? (
                        <p className="small csat-feed-notes">{response.notes}</p>
                      ) : (
                        <p className="small subtle">No comments left.</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  note,
  flag,
}: {
  label: string;
  value: string;
  note: string;
  flag?: boolean;
}) {
  return (
    <div className={`card csat-stat${flag ? ' flag' : ''}`}>
      <div className="card-body tight">
        <span className="eyebrow">{label}</span>
        <div className="csat-stat-value">{value}</div>
        <div className="xs muted">{note}</div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="csat-legend">
      {RATINGS.map((rating) => (
        <span key={rating.key}>
          <i style={{ background: rating.color }} />
          {rating.label}
        </span>
      ))}
    </div>
  );
}

function Trend({ buckets }: { buckets: CsatTrendBucket[] }) {
  if (buckets.length === 0) return <p className="small muted">No responses in this period.</p>;
  const max = buckets.reduce((m, b) => Math.max(m, b.total), 0) || 1;

  return (
    <div className="chart-scroll">
      <div className="csat-trend">
        {buckets.map((bucket) => (
          <div className="csat-tcol" key={bucket.start} title={`${bucket.label} — ${breakdown(bucket)}`}>
            <div className="xs muted">{bucket.total}</div>
            <div className="csat-tstack" style={{ height: `${Math.max(3, (bucket.total / max) * 130)}px` }}>
              {/* Bottom-up: happy at the base, unhappy on top. */}
              {RATINGS.map((rating) =>
                bucket[rating.key] ? (
                  <div key={rating.key} style={{ background: rating.color, flex: bucket[rating.key] }} />
                ) : null,
              )}
            </div>
            <div className="xs subtle">{bucket.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SortHeader({
  column,
  sort,
  onSort,
  numeric,
  children,
}: {
  column: keyof CsatClientSummary;
  sort: { column: keyof CsatClientSummary; asc: boolean };
  onSort: (column: keyof CsatClientSummary) => void;
  numeric?: boolean;
  children: React.ReactNode;
}) {
  const active = sort.column === column;
  return (
    <th className={numeric ? 'num' : undefined} aria-sort={active ? (sort.asc ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="csat-sort" onClick={() => onSort(column)}>
        {children}
        <span aria-hidden="true">{active ? (sort.asc ? ' ↑' : ' ↓') : ''}</span>
      </button>
    </th>
  );
}

/* --- Snippet builder ------------------------------------------------------ */

function SnippetBuilder() {
  const [mode, setMode] = useState<SnippetMode>('ticket');
  const [form, setForm] = useState({
    ticketId: '',
    client: '',
    customer: '',
    agent: '',
    subject: '',
    prompt: '',
    footnote: '',
  });
  /* Kept beside the typed values rather than replacing them, so switching
     modes to compare the two does not make somebody retype either. */
  const [tokens, setTokens] = useState(AUTOTASK_TOKENS);
  const fields = mode === 'template' ? tokens : form;
  const [result, setResult] = useState<CsatSnippet | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /* Writes to whichever bag the mode is showing, so switching back and forth
     preserves both the real ticket you were testing with and the variables. */
  const set = (patch: Partial<typeof form>) =>
    mode === 'template'
      ? setTokens((t) => ({ ...t, ...patch }))
      : setForm((f) => ({ ...f, ...patch }));
  const trimmed = (value: string) => (value.trim() === '' ? undefined : value.trim());

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.post<CsatSnippet>('/csat/snippet', {
          mode,
          ticketId: fields.ticketId.trim(),
          client: trimmed(fields.client),
          customer: trimmed(fields.customer),
          agent: trimmed(fields.agent),
          subject: trimmed(fields.subject),
          prompt: trimmed(form.prompt),
          footnote: trimmed(form.footnote),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The snippet could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!result) return;
    void navigator.clipboard.writeText(result.html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <>
      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Build</span>
            <h2>{mode === 'template' ? 'Block for a PSA notification template' : 'Snippet for a ticket reply'}</h2>
            <p className="sub">
              {mode === 'template' ? (
                <>
                  Paste this <strong>once</strong> into your PSA&rsquo;s notification template and
                  every mail it sends carries that ticket&rsquo;s own details. The fields below are
                  your PSA&rsquo;s variables, not values — it fills them in as it sends.
                </>
              ) : (
                <>
                  Fill in the ticket, generate, then paste the HTML into the reply using your
                  helpdesk&rsquo;s <strong>source / code view</strong> — the <code>&lt; &gt;</code>{' '}
                  button. Everything but the ticket id is optional, and everything you enter is
                  signed into the links, so it comes back attached to the response.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="card-body">
          {error && <ErrorBanner message={error} />}

          <div className="tabs mb-4">
            {MODES.map((option) => (
              <button
                key={option.key}
                type="button"
                className={mode === option.key ? 'on' : ''}
                aria-pressed={mode === option.key}
                onClick={() => {
                  setMode(option.key);
                  setResult(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {mode === 'template' && (
            <div className="mb-4">
              <p className="small">
                Use your PSA&rsquo;s own variable picker to fill these in — the defaults are
                Autotask&rsquo;s and are a starting point, not a guarantee. Two that bite:
              </p>
              <ul className="small mt-2">
                <li>
                  Pick the variable that inserts the <strong>plain number</strong>, not the one that
                  inserts a link. A &ldquo;with link&rdquo; variable emits HTML, which cannot go
                  inside a URL.
                </li>
                <li>
                  A value containing <code>&amp;</code> or <code>#</code> — &ldquo;Smith &amp;
                  Sons&rdquo; — can truncate the fields after it. The ticket number and contact are
                  ordered first so they survive; the subject is last for the same reason.
                </li>
              </ul>
            </div>
          )}

          <div className="form-grid">
            <Field
              label={mode === 'template' ? 'Ticket number variable' : 'Ticket ID'}
              hint={mode === 'template' ? 'Required. The plain number, not the linked one.' : 'Required. Whatever the PSA calls it.'}
            >
              <input
                className="input"
                value={fields.ticketId}
                onChange={(e) => set({ ticketId: e.target.value })}
                placeholder={mode === 'template' ? AUTOTASK_TOKENS.ticketId : 'INC-10432'}
              />
            </Field>
            <Field
              label={mode === 'template' ? 'Client variable' : 'Client'}
              hint="Groups the response on the Results tab. Leave blank to group by email domain."
            >
              <input
                className="input"
                value={fields.client}
                onChange={(e) => set({ client: e.target.value })}
                placeholder={mode === 'template' ? AUTOTASK_TOKENS.client : 'Acme Pty Ltd'}
              />
            </Field>
          </div>

          <div className="form-grid">
            <Field
              label={mode === 'template' ? 'Contact variable' : 'Customer email'}
              hint="Becomes the Reply-To on the notification."
            >
              <input
                className="input"
                /* Not type="email" in template mode: a variable is not an
                   address, and the browser would mark every one invalid. */
                type={mode === 'template' ? 'text' : 'email'}
                value={fields.customer}
                onChange={(e) => set({ customer: e.target.value })}
                placeholder={mode === 'template' ? AUTOTASK_TOKENS.customer : 'jane@acme.com'}
              />
            </Field>
            <Field label={mode === 'template' ? 'Agent variable' : 'Agent'}>
              <input
                className="input"
                value={fields.agent}
                onChange={(e) => set({ agent: e.target.value })}
                placeholder={mode === 'template' ? AUTOTASK_TOKENS.agent : 'Sam Rivera'}
              />
            </Field>
          </div>

          <Field
            label={mode === 'template' ? 'Ticket subject variable' : 'Ticket subject'}
            hint={mode === 'template' ? 'Last in the URL, so a stray & or # in it can only spoil itself.' : undefined}
          >
            <input
              className="input"
              value={fields.subject}
              onChange={(e) => set({ subject: e.target.value })}
              placeholder={mode === 'template' ? AUTOTASK_TOKENS.subject : 'Printer offline'}
            />
          </Field>

          <details className="mt-4">
            <summary className="small">Wording</summary>
            <div className="form-grid mt-4">
              <Field label="Heading">
                <input
                  className="input"
                  value={form.prompt}
                  onChange={(e) => set({ prompt: e.target.value })}
                  placeholder="How did we do?"
                />
              </Field>
              <Field label="Small print">
                <input
                  className="input"
                  value={form.footnote}
                  onChange={(e) => set({ footnote: e.target.value })}
                  placeholder="Click a face to leave a rating and any comments. It takes 10 seconds."
                />
              </Field>
            </div>
          </details>

          <button
            type="button"
            className="btn btn-primary mt-4"
            disabled={busy || fields.ticketId.trim() === ''}
            onClick={() => void generate()}
          >
            {busy ? 'Generating…' : mode === 'template' ? 'Generate template block' : 'Generate snippet'}
          </button>
        </div>
      </section>

      {result && (
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Ticket {result.ticketId}</span>
              <h2>Preview</h2>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
              {copied ? 'Copied' : 'Copy HTML'}
            </button>
          </div>
          <div className="card-body">
            {/* srcdoc with an empty sandbox: it renders exactly what the mail
                client will, and can neither navigate away nor run anything. */}
            <iframe className="csat-preview" title="Snippet preview" sandbox="" srcDoc={previewDoc(result.html)} />

            <div className="mt-4">
              <span className="eyebrow">HTML to paste</span>
              <textarea className="textarea mono mt-2" rows={10} readOnly spellCheck={false} value={result.html} />
            </div>

            <details className="mt-4">
              <summary className="small">Direct links — for testing, or for building your own markup</summary>
              <ul className="mt-2">
                {RATINGS.map((rating) => (
                  <li key={rating.key} className="xs mono csat-link">
                    <strong>{rating.label}</strong>{' '}
                    <a href={result.links[rating.key]} target="_blank" rel="noopener noreferrer">
                      {result.links[rating.key]}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </section>
      )}
    </>
  );
}

/* --- Helpers -------------------------------------------------------------- */

const previewDoc = (html: string) =>
  `<body style="margin:0;padding:16px;background:#fff;font-family:'Open Sans',Arial,sans-serif;">${html}</body>`;

const breakdown = (counts: { green: number; yellow: number; red: number }) =>
  RATINGS.map((r) => `${counts[r.key]} ${r.label.toLowerCase()}`).join(', ');

/** Mirrors the API's fallback so the feed never shows a blank client. */
function domainOf(email: string | undefined): string {
  if (!email) return 'Unattributed';
  const at = email.lastIndexOf('@');
  return at > 0 ? email.slice(at + 1).toLowerCase() : 'Unattributed';
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function relative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return shortDate(iso);
}

/** Fallback heading when a range was linked without a label. */
function describeWindow(from: string | null, to: string | null): string {
  if (from && to) return `${shortDate(from)} to ${shortDate(to)}`;
  if (from) return `Since ${shortDate(from)}`;
  if (to) return `Up to ${shortDate(to)}`;
  return 'All time';
}
